import { LockButton } from 'components/FieldLock/LockButton'
import { type LockScope, isExplicitlyUnlocked } from 'components/FieldLock/types'
import { RichTextField } from 'components/RichTextEditor/RichTextField'
import type { InputType } from 'config'
import { useLockContext } from 'context/LockContext'
import { usePermissions } from 'hooks/usePermissions'
import { store } from 'reducers'
import {
  ActionButton,
  AutoComplete,
  ButtonGroup,
  Checkbox,
  EmailListInput,
  Input2,
  InputDateRange,
  InputEmail,
  InputFile,
  InputFileTable,
  InputRating,
  InputVisibleLogic,
  InputWithButton,
  Label,
  MultiSelect,
  OptionSelect,
  RadioGroup,
  SearchableSelect,
  Section,
  Select2,
  SelectOptionInputs,
  TextArea,
  Toggle,
  ToggleButton,
} from 'stories/components'
import { ButtonGroupSelector } from 'stories/components/ButtonGroupSelector/ButtonGroupSelector'
import { InputFormTable } from 'stories/components/FormTable/InputFormTable'
import { GoogleAutoComplete } from 'stories/components/GoogleAutoComplete/GoogleAutoComplete'
import { GoogleMap } from 'stories/components/GoogleMap'
import { hasAuthPermission } from 'utils/hasAuthPermission'

interface Prop {
  input: InputType
  Key: string
  onChange: Function
  showHistory?: Function
  onBlur?: Function | null
  onFocus?: Function | null
  onKeyUp?: Function | null
  onClick?: Function | null
  lockScope?: LockScope
}

export const RenderInput = (props: Prop) => {
  const {
    input,
    Key,
    onChange,
    showHistory = () => {},
    onBlur = null,
    onFocus = null,
    onKeyUp = null,
    onClick = null,
    lockScope,
  } = props

  const { locks, isLocked, canLock, setLock } = useLockContext()
  let {
    fontSize,
    history,
    prefix,
    title,
    rows,
    error = '',
    disabled,
    value = '',
    icon = '',
    readOnly = false,
    sort = false,
    copyable = false,
    defaultSelected = undefined,
    multiple = false,
    needCountyUpdate = true,
    tooltip = '',
    acceptFileTypes,
    required,
    additionalElements,
    bgColor,
    placeholder,
    autoComplete = false,
    isHyperLink = false,
  } = input as any
  required = required && !disabled
  if (!(input.inputType as any)) input.inputType = 'Text' as any

  try {
    const { hasPermission } = usePermissions()
    if (!hasPermission('Global.AuditLog')) history = false
  } catch (e) {
    try {
      const auth = (store.getState() as any)?.auth
      if (!hasAuthPermission(auth, 'Global.AuditLog')) history = false
    } catch (_) {
      history = false
    }
  }

  const fieldIsLocked = lockScope ? isLocked(lockScope) : false
  if (fieldIsLocked) {
    disabled = true
    required = false
  }

  const fieldIsExplicitlyUnlocked = lockScope ? isExplicitlyUnlocked(locks, lockScope) : false
  if (fieldIsExplicitlyUnlocked) {
    disabled = false
  }

  if (lockScope && canLock) {
    const lockBtn = (
      <span className="ml-1 hidden group-hover:inline-flex items-center">
        <LockButton locked={!!disabled} canLock={canLock} onToggle={() => setLock(lockScope!, !disabled)} size="sm" />
      </span>
    )
    additionalElements = additionalElements ? (
      <>
        {additionalElements}
        {lockBtn}
      </>
    ) : (
      lockBtn
    )
  }

  switch (input.inputType) {
    case 'Text':
    case 'text':
      return (
        <Input2
          autoComplete={autoComplete}
          title={title}
          bgColor={bgColor}
          prefix={prefix}
          hasIcon={icon !== ''}
          icon={icon}
          key={Key}
          name={Key}
          type={input.type}
          error={error}
          value={value as string}
          fontSize={fontSize}
          disabled={disabled}
          readOnly={readOnly}
          tooltip={tooltip}
          required={required}
          history={history}
          copyable={copyable}
          additionalElements={additionalElements}
          isHyperLink={isHyperLink}
          onChange={(value) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
          onBlurNull={onBlur === null}
          showHistory={() => showHistory(Key)}
          placeholder={placeholder}
          indent={input.indent}
          options={input.options}
          showLinkDir={input.showLinkDir}
          userSearch={input.userSearch}
          onFocus={(event) => onFocus && onFocus(event, Key)}
          onKeyUp={(event) => onKeyUp && onKeyUp(event)}
          onClick={(event) => onClick && onClick(event)}
          decimalStep={input.decimalStep}
        />
      )
    case 'Select':
    case 'select':
      return (
        <Select2
          id={Key}
          bgColor={bgColor}
          name={Key}
          title={title}
          options={input.options}
          key={Key}
          error={error}
          disabled={disabled}
          readOnly={readOnly}
          sort={sort}
          tooltip={tooltip}
          value={input.value as string}
          hasDefaultOption={input.hasDefaultOption}
          defaultOptionText={input.defaultOptionText}
          required={required}
          history={history}
          copyable={copyable}
          onChange={(value) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
          showHistory={() => showHistory(Key)}
          additionalElements={additionalElements}
        />
      )
    case 'OptionSelect':
      return (
        <OptionSelect
          key={Key}
          title={title}
          options={input.options}
          disabled={disabled}
          additionalElements={additionalElements}
          onChange={(value) => onChange(Key, value)}
          sort={sort}
          value={value}
        />
      )
    case 'MultiSelect':
    case 'multiselect':
      return (
        <MultiSelect
          id={Key}
          title={title}
          options={input.options}
          key={Key}
          error={error}
          disabled={disabled}
          readOnly={readOnly}
          tooltip={tooltip}
          value={input.value as Record<string, boolean>}
          required={required}
          additionalElements={additionalElements}
          history={history}
          onChange={(value) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
          showHistory={() => showHistory(Key)}
          defaultSelected={defaultSelected}
          sort={input.sort}
          sortKey={input.sortKey}
          prefix={input.prefix}
          className={input.className}
          allowCustomValues={input.allowCustomValues}
          customValuePlaceholder={input.customValuePlaceholder}
          customValueValidator={input.customValueValidator}
        />
      )
    case 'SearchableSelect':
    case 'searchableselect':
      return (
        <SearchableSelect
          id={Key}
          bgColor={bgColor}
          name={Key}
          title={title}
          options={input.options}
          key={Key}
          error={error}
          disabled={disabled}
          readOnly={readOnly}
          sort={sort}
          tooltip={tooltip}
          value={input.value as string}
          hasDefaultOption={input.hasDefaultOption}
          defaultOptionText={input.defaultOptionText}
          required={required}
          history={history}
          copyable={copyable}
          onChange={(value) => onChange(Key, value)}
          showHistory={() => showHistory(Key)}
          additionalElements={additionalElements}
        />
      )
    case 'Check':
    case 'check':
      return (
        <ToggleButton
          className="pt-1"
          id={Key}
          title={title}
          key={Key}
          disabled={disabled}
          error={error}
          tooltip={tooltip}
          value={input.value as boolean}
          history={history}
          additionalElements={additionalElements}
          onChange={(value) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
          showHistory={() => showHistory(Key)}
          label={['Yes', 'No']}
          textLeft={input.textLeft}
        />
      )
    case 'CheckBox':
    case 'checkbox':
      return (
        <Checkbox
          className={`pt-1 ${input.className}`}
          id={Key}
          title={title}
          color={input.color}
          key={Key}
          disabled={disabled}
          value={input.value as boolean}
          history={history}
          error={error}
          tooltip={tooltip}
          additionalElements={additionalElements}
          onChange={(value) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
          showHistory={() => showHistory(Key)}
          size={input.size}
          fontClass={input.fontSize ? `text-[${input.fontSize}px]` : ''}
        />
      )
    case 'Radio':
    case 'radio':
      return (
        <RadioGroup
          id={Key}
          title={title}
          options={input.options}
          optionTooltips={input.optionTooltips}
          key={Key}
          name={Key}
          error={error}
          disabled={disabled}
          value={input.value as string}
          required={required}
          history={history}
          size={input.size}
          titleType={input.titleType}
          additionalElements={additionalElements}
          onChange={(value) => onChange(Key, value)}
          showHistory={() => showHistory(Key)}
          noBorder={input.noBorder}
          isPlain={input.isPlain}
        />
      )
    case 'TextArea':
    case 'textarea':
      return (
        <TextArea
          className="pt-1"
          title={title}
          key={Key}
          rows={rows}
          error={error}
          disabled={disabled}
          value={input.value as string}
          history={history}
          required={required}
          readOnly={readOnly}
          additionalElements={additionalElements}
          onBlur={() => onBlur && onBlur(Key)}
          onBlurNull={onBlur === null}
          onChange={(value) => onChange(Key, value)}
          showHistory={() => showHistory(Key)}
          onFocus={(event) => onFocus && onFocus(event, Key)}
          onKeyUp={(event) => onKeyUp && onKeyUp(event)}
          onClick={(event) => onClick && onClick(event)}
        />
      )
    case 'RichText':
    case 'richtext':
      return (
        <RichTextField
          key={Key}
          title={title}
          value={(input.value as string) || ''}
          error={error}
          required={required}
          disabled={disabled || readOnly}
          placeholders={(input as any).placeholders || []}
          minHeight={(input as any).minHeight}
          variant={(input as any).variant || 'inline'}
          additionalElements={additionalElements}
          onChange={(value) => onChange(Key, value)}
        />
      )
    case 'Map':
    case 'map':
      return (
        <GoogleAutoComplete
          title={title}
          key={Key}
          inputKey={Key}
          error={error}
          value={value as string}
          disabled={disabled}
          required={required}
          readOnly={readOnly}
          needCountyUpdate={needCountyUpdate}
          tooltip={tooltip}
          requireValidate={input.requireValidate}
          allowForeign={input.allowForeign}
          sitexPro={input.sitexPro}
          onChange={(value) => onChange(Key, value)}
          additionalElements={additionalElements}
          onBlur={() => onBlur && onBlur(Key)}
          history={history}
          showHistory={() => showHistory(Key)}
        />
      )
    case 'GoogleMap':
    case 'googleMap':
      return <GoogleMap key={Key} markers={[{ title: title || '', lat: value.lat, lng: value.lng }]} />
    case 'Toggle':
    case 'toggle':
      return (
        <Toggle
          className="pt-1"
          id={Key}
          title={title}
          key={Key}
          disabled={disabled}
          error={error}
          value={input.value as boolean}
          history={history}
          additionalElements={additionalElements}
          onChange={(value) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
          showHistory={() => showHistory(Key)}
        />
      )
    case 'ToggleButton':
    case 'togglebutton':
      return (
        <ToggleButton
          className="pt-1"
          id={Key}
          title={title}
          key={Key}
          disabled={disabled}
          error={error}
          value={input.value as boolean}
          history={history}
          additionalElements={additionalElements}
          onChange={(value) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
          showHistory={() => showHistory(Key)}
          label={input.label}
          textLeft={input.textLeft}
          tooltip={tooltip}
        />
      )
    case 'ActionButton':
    case 'actionbutton':
      return (
        <ActionButton
          key={Key}
          field={(input as any).field || (input as any)}
          value={(input as any).value}
          onChange={(value) => onChange(Key, value, true)}
          onBlur={() => onBlur && onBlur(Key)}
          showHistory={() => showHistory(Key)}
          loading={(input as any).loading}
          disabled={disabled}
          additionalElements={additionalElements}
        />
      )
    case 'File':
    case 'file':
      return (
        <InputFile
          className="pt-1"
          title={title}
          key={Key}
          acceptFileTypes={acceptFileTypes}
          disabled={disabled}
          value={input.value}
          error={error}
          multiple={multiple}
          history={history}
          additionalElements={additionalElements}
          onChange={(value) => onChange(Key, value)}
          showHistory={() => showHistory(Key)}
          downloadable={input.downloadable}
          acceptDocument={input.acceptDocument}
        />
      )
    case 'FileTable':
    case 'filetable':
      return (
        <InputFileTable
          className="pt-1"
          title={title}
          filePath={input.filePath}
          key={Key}
          name={Key}
          acceptFileTypes={acceptFileTypes}
          disabled={disabled}
          value={input.value}
          error={error}
          history={history}
          onChange={(value) => onChange(Key, value, true)}
          onBlur={() => onBlur && onBlur(Key)}
          showHistory={() => showHistory(Key)}
          showCategory={input.showCategory}
          showStatus={input.showStatus}
          disableNameChange={input.disableNameChange}
          multiple={input.multiple}
          categories={input.categories}
          customFields={input.customFields}
          additionalActions={input.additionalActions || input.additionalElements}
          additionalRowActions={input.additionalRowActions}
          canDelete={input.canDelete}
          canEditCategory={input.canEditCategory}
          onTitleClick={(input as any).onTitleClick}
          type={input.type}
          selectable={(input as any).selectable}
          selectedKeys={(input as any).selectedKeys}
          onSelectionChange={(input as any).onSelectionChange}
        />
      )
    case 'AutoComplete':
    case 'autocomplete':
      return (
        <AutoComplete
          className="pt-1"
          title={title}
          key={Key}
          value={value}
          disabled={disabled}
          error={error}
          type={input.type}
          tooltip={tooltip}
          additionalElements={additionalElements}
          userSearch={input.userSearch}
          autoCompleteType={input.autoCompleteType}
          required={input.required}
          onBlur={() => onBlur && onBlur(Key)}
          history={history}
          onChange={(value) => onChange(Key, value)}
          showHistory={() => showHistory(Key)}
          checkExisting={input.checkExisting}
        />
      )
    case 'InputWithButton':
    case 'inputwithbutton':
      return (
        <InputWithButton
          className=""
          title={title}
          key={Key}
          value={value}
          disabled={disabled}
          multiple={input.multiple}
          error={error}
          type={input.type}
          required={input.required}
          options={input.options}
          modalTitle={input.modalTitle}
          enableInput={input.enableInput}
          searchable={input.searchable}
          additionalElements={additionalElements}
          onChange={(value: any) => onChange(Key, value)}
          showHistory={() => showHistory && showHistory(Key)}
          history={history}
        />
      )
    case 'email':
      return (
        <InputEmail
          className=""
          title={title}
          key={Key}
          value={value}
          disabled={disabled}
          error={error}
          required={input.required}
          defaultEmails={input.defaultEmails}
          modalTitle={input.modalTitle}
          additionalElements={additionalElements}
          onChange={(value: any) => onChange(Key, value)}
        />
      )
    case 'Table':
    case 'table':
      return (
        <InputFormTable
          inputs={input.inputs}
          data={input.value}
          onChange={(value: any) => onChange(Key, value)}
          error={error}
          permission={disabled ? 3 : 1}
        />
      )

    case 'Section':
    case 'section':
      return <Section title={title} additionalElements={additionalElements} />

    case 'Group':
    case 'group':
      return (
        <div className={`w-full bg-gray-200 p-2`}>
          <p>{title}</p>
        </div>
      )
    case 'Label':
    case 'label':
      return <Label title={title} additionalElements={additionalElements} />

    case 'Title':
    case 'title':
      return (
        <div className={`w-full p-2 text-sm`}>
          <p dangerouslySetInnerHTML={{ __html: title }}></p>
        </div>
      )

    case 'Value':
    case 'value':
      return (
        <div className={`w-full p-2`}>
          <p dangerouslySetInnerHTML={{ __html: value }}></p>
        </div>
      )

    case 'DateRange':
    case 'daterange':
      return (
        <InputDateRange
          title={title}
          name={Key}
          value={(value as { from?: string; to?: string }) || {}}
          disabled={disabled}
          readOnly={readOnly}
          tooltip={tooltip}
          onChange={(value) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
        />
      )
    case 'Custom':
    case 'custom':
      return input.render ? input.render(input.value, (value: any) => onChange(Key, value), input) : null
    case 'alert':
      return (
        <div className={`input md:col-span-${input.span} mb-4`}>
          <div
            className="bg-gray-100 border border-gray-400 text-gray-700 px-4 py-3 rounded relative text-[15px]"
            role="alert"
          >
            <span dangerouslySetInnerHTML={{ __html: input.value }} />
          </div>
        </div>
      )

    case 'Rating':
    case 'rating':
      return (
        <InputRating
          title={input.title}
          max={input.max}
          value={input.value}
          required={input.required}
          error={error}
          disabled={disabled}
          readOnly={readOnly}
          onChange={(value: any) => onChange(Key, value)}
        />
      )

    case 'ButtonGroup':
    case 'buttongroup':
      return (
        <ButtonGroup
          title={input.options}
          className={input.className}
          value={input.value}
          onChange={(value: any) => onChange(Key, value)}
        />
      )

    case 'ButtonGroupSelector':
    case 'buttongroupselector':
      return (
        <ButtonGroupSelector
          title={input.title}
          options={input.options}
          className={input.className}
          value={input.value}
          disabled={input.disabled}
          required={input.required}
          error={input.error}
          history={history}
          onChange={(value: any) => onChange(Key, value)}
          showHistory={() => showHistory(Key)}
        />
      )
    case 'VisibleLogic':
    case 'visibleLogic':
      return (
        <InputVisibleLogic
          className="pt-1"
          id={Key}
          title={title}
          key={Key}
          disabled={disabled}
          error={error}
          value={input.value}
          loanFieldProperties={input.loanFieldProperties}
          fieldTitles={input.fieldTitles}
          fieldInputs={input.fieldInputs}
          options={input.options}
          history={history}
          onChange={(value) => onChange(Key, value)}
          showHistory={() => showHistory(Key)}
        />
      )

    case 'EmailList':
      return (
        <EmailListInput
          key={Key}
          title={title}
          value={Array.isArray(value) ? (value as string[]) : []}
          disabled={disabled}
          readOnly={readOnly}
          tooltip={tooltip}
          placeholder={placeholder}
          onChange={(value: string[]) => onChange(Key, value)}
          onBlur={() => onBlur && onBlur(Key)}
        />
      )

    case 'SelectOptionInput':
      return (
        <SelectOptionInputs
          title={input.title}
          value={Array.isArray(input.value) ? input.value : []}
          allCanDelete={input.allCanDelete}
          minLength={input.minLength}
          showKey={input.showKey}
          showVisibleBtn={input.showVisibleBtn}
          additionalElements={input.additionalElements}
          showAdjustments={input.showAdjustments}
          onChange={(value: any) => onChange(Key, value)}
        />
      )
  }
  return null
}
